import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import themeGet from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';
import { DropdownMenu } from '@radix-ui/themes';
import { QRCodeCanvas } from 'qrcode.react';

import {
  SITE_ADMIN,
  RESTRICTED_SITE_ADMIN,
  CLIENT_ADMIN,
} from '../../../../lib/utils/constants/UserTypes';
import { ConfirmArchiveModal } from './ConfirmArchiveModal.jsx';
import { ConfirmDeleteModal } from './ConfirmDeleteModal.jsx';
import CreateHighlightModal from './CreateHighlightModal.jsx';
import {
  useCreateTemplateMutation,
  useUnarchiveCampaignMutation,
  useRestoreCampaignMutation,
} from './hooks/useCampaignMutation';
import styles from './campaigns_table.module.css';
import { useNotification } from '../../../../shared/Notification/useNotification';
import { useCampaignsTableContext } from './CampaignsTableContext.jsx';
import { tabs } from './constants';

const ActiveActions = ({
  userType,
  uuid,
  shareLink,
  duplicateCampaign,
  setShowArchiveModal,
  setShowDeleteModal,
  isClientAdmin,
}) => {
  const { t } = useTranslation('campaigns');
  const { showSuccess } = useNotification();
  const { createTemplateMutation } = useCreateTemplateMutation();

  const [href, setHref] = useState('');

  const canvasRef = useRef();

  useEffect(() => {
    const generateHref = () => {
      if (!canvasRef.current) return '';

      const canvas = canvasRef.current;
      return canvas.toDataURL('image/png');
    };
    setHref(generateHref());
  }, [canvasRef]);

  const allowCreateTemplate =
    userType === SITE_ADMIN || userType === RESTRICTED_SITE_ADMIN;

  const copyHighlightLink = () => {
    navigator.clipboard.writeText(shareLink);
  };

  return (
    <>
      {!isClientAdmin && (
        <DropdownMenu.Item
          onClick={e => {
            e.stopPropagation();
            duplicateCampaign();
          }}
        >
          {t('tableOverview.duplicate')}
        </DropdownMenu.Item>
      )}
      {allowCreateTemplate && (
        <DropdownMenu.Item
          onClick={e => {
            e.stopPropagation();
            createTemplateMutation(uuid);
          }}
        >
          {t('tableOverview.createTemplate')}
        </DropdownMenu.Item>
      )}
      <DropdownMenu.Item
        data-cy="archive"
        onClick={e => {
          e.stopPropagation();
          setShowArchiveModal(true);
        }}
      >
        {t('tableOverview.archive')}
      </DropdownMenu.Item>
      <DropdownMenu.Item
        data-cy="delete"
        onClick={e => {
          e.stopPropagation();
          setShowDeleteModal(true);
        }}
      >
        {t('tableOverview.delete')}
      </DropdownMenu.Item>
      <DropdownMenu.Separator />
      <DropdownMenu.Item
        onClick={e => {
          e.stopPropagation();
          showSuccess({ content: t('tableOverview.copyLinkSuccess') });
          copyHighlightLink();
        }}
      >
        {t('tableOverview.copyLink')}
      </DropdownMenu.Item>
      <DropdownMenu.Item style={{ gap: 0 }}>
        <div className={styles.qrCodeContainer} id={shareLink}>
          <QRCodeCanvas marginSize={4} ref={canvasRef} value={shareLink} />
        </div>
        <a href={href} download="qrcode.png" className={styles.qrCodeLink}>
          {t('tableOverview.downloadQRCode')}
        </a>
      </DropdownMenu.Item>
    </>
  );
};

const ArchivedActions = ({
  isClientAdmin,
  duplicateCampaign,
  unarchiveCampaign,
  setShowDeleteModal,
}) => {
  const { t } = useTranslation('campaigns');

  return (
    <>
      {!isClientAdmin && (
        <DropdownMenu.Item
          onClick={e => {
            e.stopPropagation();
            duplicateCampaign();
          }}
        >
          {t('tableOverview.duplicate')}
        </DropdownMenu.Item>
      )}
      <DropdownMenu.Item
        onClick={e => {
          e.stopPropagation();
          unarchiveCampaign();
        }}
      >
        {t('tableOverview.unarchive')}
      </DropdownMenu.Item>
      <DropdownMenu.Item
        onClick={e => {
          e.stopPropagation();
          setShowDeleteModal(true);
        }}
      >
        {t('tableOverview.delete')}
      </DropdownMenu.Item>
    </>
  );
};

const DeletedActions = ({ uuid, isArchived }) => {
  const { t } = useTranslation('campaigns');
  const {
    switchToTab,
    highlightCampaign,
    clearStatusFilters,
    setStatusFilters,
  } = useCampaignsTableContext();
  const { restoreCampaignMutation } = useRestoreCampaignMutation({
    onSuccessCallback: () => {
      if (isArchived) {
        setStatusFilters(['archived']);
      } else {
        clearStatusFilters();
      }
      switchToTab(tabs.active.name);
      setTimeout(() => {
        highlightCampaign(uuid);
      }, 100);
    },
  });

  return (
    <>
      <DropdownMenu.Item
        onClick={e => {
          e.stopPropagation();
          restoreCampaignMutation(uuid);
        }}
      >
        {t('tableOverview.restore')}
      </DropdownMenu.Item>
    </>
  );
};

const RowAction = ({
  filter,
  userType,
  uuid,
  name,
  slug,
  demoSlug,
  onArchiveModalChange,
  disabled,
  tooltipText,
  isArchived,
  isNewHire = false,
  isMultiMonth = false,
}) => {
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const {
    switchToTab,
    highlightCampaign,
    clearStatusFilters,
  } = useCampaignsTableContext();
  const shareLink = `https://${
    import.meta.env.VITE_APP_HOST
  }/board/${demoSlug}/${slug}`;

  useEffect(() => {
    if (onArchiveModalChange) {
      onArchiveModalChange(showArchiveModal);
    }
  }, [showArchiveModal, onArchiveModalChange]);

  const isClientAdmin = userType === CLIENT_ADMIN;

  const { unarchiveCampaignMutation } = useUnarchiveCampaignMutation({
    onSuccessCallback: () => {
      // Drop Archived (or any) status filter so the row is visible on Active.
      clearStatusFilters();
      switchToTab(tabs.active.name);
      setTimeout(() => {
        highlightCampaign(uuid);
      }, 100);
    },
  });

  const unarchiveCampaign = () => {
    unarchiveCampaignMutation(uuid);
  };

  const duplicateCampaign = () => {
    setShowDuplicateModal(true);
  };

  const handleDuplicateModalClose = () => {
    setShowDuplicateModal(false);
  };

  const handleDuplicateSuccess = () => {
    setShowDuplicateModal(false);
  };

  const getActionsForFilter = (filter, props) => {
    const {
      userType,
      uuid,
      shareLink,
      duplicateCampaign,
      unarchiveCampaign,
      setShowArchiveModal,
      setShowDeleteModal,
      isClientAdmin,
      isArchived,
    } = props;

    if (filter === 'recoverable') {
      return <DeletedActions uuid={uuid} isArchived={isArchived} />;
    }

    if (isArchived) {
      return (
        <ArchivedActions
          isClientAdmin={isClientAdmin}
          duplicateCampaign={duplicateCampaign}
          unarchiveCampaign={unarchiveCampaign}
          setShowDeleteModal={setShowDeleteModal}
        />
      );
    }

    return (
      <ActiveActions
        userType={userType}
        uuid={uuid}
        shareLink={shareLink}
        duplicateCampaign={duplicateCampaign}
        setShowArchiveModal={setShowArchiveModal}
        setShowDeleteModal={setShowDeleteModal}
        isClientAdmin={isClientAdmin}
      />
    );
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <ButtonHolder
          data-cy="dropDown"
          className="dropdown-container"
          disabled={disabled}
          {...(tooltipText && {
            'data-tip': tooltipText,
            'data-for': 'calendar-tooltip',
          })}
        >
          <EllipsisIcon alt="ellipsis" disabled={disabled} />
        </ButtonHolder>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content>
        <div className="dropdown-menu">
          {getActionsForFilter(filter, {
            userType,
            uuid,
            shareLink,
            duplicateCampaign,
            unarchiveCampaign,
            setShowArchiveModal,
            setShowDeleteModal,
            isClientAdmin,
            isArchived,
          })}
        </div>
      </DropdownMenu.Content>

      <ConfirmArchiveModal
        uuid={uuid}
        name={name}
        showArchiveModal={showArchiveModal}
        setShowArchiveModal={setShowArchiveModal}
      />

      <ConfirmDeleteModal
        uuid={uuid}
        name={name}
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
      />

      <CreateHighlightModal
        isOpen={showDuplicateModal}
        onClose={handleDuplicateModalClose}
        onCreateHighlight={handleDuplicateSuccess}
        campaignName={name}
        campaignUuid={uuid}
        isNewHire={isNewHire}
        isMultiMonth={isMultiMonth}
      />
    </DropdownMenu.Root>
  );
};

export default RowAction;

const ButtonHolder = styled.div`
  font-size: 10px;
  position: relative;
  float: right;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EllipsisIcon = styled.i.attrs({
  className: 'fa-regular fa-ellipsis-v fa-2x',
})`
  min-width: 22.5px;
  max-width: 22.5px;
  border: 1px solid transparent;
  border-radius: 14px;
  color: ${props =>
    props.disabled ? themeGet('colors.black.30') : 'var(--gray-9)'};
  text-align: center;
  &:hover {
    color: ${props =>
      props.disabled ? themeGet('colors.black.30') : 'var(--accent-9)'};
  }
`;
